import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Check, Loader2, Search, Tag, X } from 'lucide-react';
import type { SubscriptionTopic } from '../../../types/api';
import { useUiLocale } from '../../../contexts/UiLocaleContext';

/** Same chip palette as /subscribers topic badges. */
export const TOPIC_CHIP_PALETTE = [
  'bg-blue-100 text-blue-700 border-blue-200',
  'bg-orange-100 text-orange-700 border-orange-200',
  'bg-sky-100 text-sky-700 border-sky-200',
  'bg-green-100 text-green-700 border-green-200',
  'bg-rose-100 text-rose-700 border-rose-200',
  'bg-purple-100 text-purple-700 border-purple-200',
  'bg-amber-100 text-amber-700 border-amber-200',
] as const;

const COLOR_TOKEN_PALETTE: Record<string, string> = {
  blue: 'bg-blue-100 text-blue-700 border-blue-200',
  purple: 'bg-purple-100 text-purple-700 border-purple-200',
  green: 'bg-green-100 text-green-700 border-green-200',
  orange: 'bg-orange-100 text-orange-700 border-orange-200',
  rose: 'bg-rose-100 text-rose-700 border-rose-200',
  cyan: 'bg-cyan-100 text-cyan-700 border-cyan-200',
};

const HEX_TO_CHIP: Record<string, string> = {
  '#2563eb': COLOR_TOKEN_PALETTE.blue,
  '#3b82f6': COLOR_TOKEN_PALETTE.blue,
  '#1d4ed8': COLOR_TOKEN_PALETTE.blue,
  '#7c3aed': COLOR_TOKEN_PALETTE.purple,
  '#a855f7': COLOR_TOKEN_PALETTE.purple,
  '#8b5cf6': COLOR_TOKEN_PALETTE.purple,
  '#16a34a': COLOR_TOKEN_PALETTE.green,
  '#22c55e': COLOR_TOKEN_PALETTE.green,
  '#059669': COLOR_TOKEN_PALETTE.green,
  '#ea580c': COLOR_TOKEN_PALETTE.orange,
  '#f97316': COLOR_TOKEN_PALETTE.orange,
  '#e11d48': COLOR_TOKEN_PALETTE.rose,
  '#f43f5e': COLOR_TOKEN_PALETTE.rose,
  '#0891b2': COLOR_TOKEN_PALETTE.cyan,
  '#06b6d4': COLOR_TOKEN_PALETTE.cyan,
  '#0ea5e9': COLOR_TOKEN_PALETTE.cyan,
};

function stablePaletteIndex(seed: string): number {
  let h = 0;
  for (let i = 0; i < seed.length; i += 1) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return h % TOPIC_CHIP_PALETTE.length;
}

/**
 * Resolve API topic color → Tailwind chip classes (matches /subscribers).
 * Accepts full `bg-…` classes, short tokens (`blue`), or hex.
 */
export function topicChipClass(color: string | null | undefined, fallbackIdx = 0): string {
  if (typeof color === 'string' && color.trim()) {
    const c = color.trim();
    if (c.includes('bg-')) return c;
    if (COLOR_TOKEN_PALETTE[c]) return COLOR_TOKEN_PALETTE[c];
    if (c.startsWith('#')) {
      return HEX_TO_CHIP[c.toLowerCase()] ?? TOPIC_CHIP_PALETTE[stablePaletteIndex(c.toLowerCase())];
    }
  }
  return TOPIC_CHIP_PALETTE[Math.abs(fallbackIdx) % TOPIC_CHIP_PALETTE.length];
}

/** @deprecated Prefer topicChipClass — kept for callers that still need inline hex tint. */
export function topicChipStyle(color: string | null | undefined): React.CSSProperties {
  const c = color || '#64748b';
  return { backgroundColor: `${c}1A`, color: c, borderColor: `${c}55` };
}

type PanelPosition = { top: number; left: number; width: number; maxHeight: number; openUp: boolean };

/** Fixed-viewport coordinates for the dropdown so it can escape clipped (overflow-hidden) ancestors. */
function computePanelPosition(rect: DOMRect, preferUp = false): PanelPosition {
  const margin = 8;
  const maxPanelHeight = 224; // matches max-h-56
  const width = Math.max(rect.width, 192); // min 12rem
  const maxLeft = window.innerWidth - width - margin;
  const left = Math.min(Math.max(margin, rect.left), Math.max(margin, maxLeft));
  const spaceBelow = window.innerHeight - rect.bottom - margin;
  const spaceAbove = rect.top - margin;
  const openUp = preferUp || (spaceBelow < 160 && spaceAbove > spaceBelow);
  const maxHeight = Math.min(maxPanelHeight, openUp ? spaceAbove : spaceBelow);
  const top = openUp ? rect.top - margin : rect.bottom + 4;
  return { top, left, width, maxHeight, openUp };
}

interface TopicTagInputProps {
  tags: string[];
  onChange: (tags: string[]) => void;
  placeholder?: string;
}

/** Free-text tag input for topic names (creates new topics on submit). */
export function TopicTagInput({ tags, onChange, placeholder }: TopicTagInputProps) {
  const [draft, setDraft] = useState('');

  const commitDraft = () => {
    const value = draft.trim().replace(/,$/, '');
    if (!value) {
      setDraft('');
      return;
    }
    const exists = tags.some((t) => t.toLowerCase() === value.toLowerCase());
    if (!exists) onChange([...tags, value]);
    setDraft('');
  };

  const removeAt = (index: number) => {
    onChange(tags.filter((_, i) => i !== index));
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      commitDraft();
      return;
    }
    if (e.key === 'Backspace' && !draft && tags.length > 0) {
      onChange(tags.slice(0, -1));
    }
  };

  return (
    <div className="flex min-h-[2.25rem] flex-wrap items-center gap-1 rounded-lg border border-white/60 bg-white/70 px-2 py-1 text-sm shadow-sm backdrop-blur-sm transition-colors focus-within:ring-1 focus-within:ring-blue-200/60">
      {tags.map((tag, i) => (
        <span
          key={`${tag}-${i}`}
          className={`inline-flex max-w-full items-center gap-0.5 rounded-full border px-1.5 py-0 text-[10px] font-medium ${topicChipClass(null, i)}`}
        >
          <span className="truncate">{tag}</span>
          <button
            type="button"
            onClick={() => removeAt(i)}
            className="rounded-full p-0.5 hover:bg-black/10"
            aria-label={`Remove ${tag}`}
          >
            <X className="h-2.5 w-2.5" />
          </button>
        </span>
      ))}
      <input
        id="topics"
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={onKeyDown}
        onBlur={commitDraft}
        placeholder={tags.length === 0 ? placeholder : undefined}
        className="min-w-[6rem] flex-1 border-0 bg-transparent p-0.5 text-sm outline-none placeholder:text-slate-500"
      />
    </div>
  );
}

interface TopicMultiSelectProps {
  options: SubscriptionTopic[];
  selectedIds: number[];
  onChange: (ids: number[]) => void;
  placeholder?: string;
  /** Compact trigger for inline (table-row) use. */
  compact?: boolean;
}

export function TopicMultiSelect({
  options,
  selectedIds,
  onChange,
  placeholder,
  compact = false,
}: TopicMultiSelectProps) {
  const { t } = useUiLocale();
  const resolvedPlaceholder = placeholder ?? t('topicMultiSelect.placeholder');
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<PanelPosition | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const updatePosition = useCallback(() => {
    if (triggerRef.current) setPosition(computePanelPosition(triggerRef.current.getBoundingClientRect()));
  }, []);

  // Position the portal panel before paint, then keep it pinned to the trigger.
  useLayoutEffect(() => {
    if (open) updatePosition();
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return;
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [open, updatePosition]);

  // The panel renders in a portal, so the outside check must include both the trigger and the panel.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (rootRef.current?.contains(target)) return;
      if (panelRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const selected = options.filter((o) => selectedIds.includes(o.id));

  const toggle = (id: number) => {
    onChange(
      selectedIds.includes(id)
        ? selectedIds.filter((x) => x !== id)
        : [...selectedIds, id],
    );
  };

  return (
    <div ref={rootRef} className="relative">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`flex w-full items-center justify-between gap-1 rounded-lg border border-white/60 bg-white/70 text-sm shadow-sm backdrop-blur-sm transition-colors hover:bg-white/90 focus:outline-none focus:ring-1 focus:ring-blue-200/60 ${
          compact ? 'px-1.5 py-0.5' : 'px-2.5 py-1.5'
        }`}
      >
        <span className="flex min-w-0 flex-1 flex-wrap items-center gap-1">
          {selected.length === 0 ? (
            <span className="truncate text-slate-500">{resolvedPlaceholder}</span>
          ) : (
            selected.map((t) => (
              <span
                key={t.id}
                className={`truncate rounded-full border px-1.5 py-0 text-[10px] font-medium ${topicChipClass(t.color, t.id)}`}
              >
                {t.topic}
              </span>
            ))
          )}
        </span>
        <ChevronDown className="h-3.5 w-3.5 shrink-0 text-slate-500" />
      </button>

      {open && position && createPortal(
        <div
          ref={panelRef}
          style={{
            position: 'fixed',
            top: position.top,
            left: position.left,
            width: position.width,
            maxHeight: position.maxHeight,
            transform: position.openUp ? 'translateY(-100%)' : undefined,
          }}
          className="z-50 overflow-y-auto rounded-lg border border-white/60 bg-white/95 p-1 shadow-lg backdrop-blur-xl"
        >
          {options.length === 0 ? (
            <p className="px-2 py-3 text-center text-xs text-slate-500">
              {t('topicMultiSelect.empty')}
            </p>
          ) : (
            options.map((o) => {
              const isSel = selectedIds.includes(o.id);
              return (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => toggle(o.id)}
                  className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs hover:bg-slate-100"
                >
                  <span
                    className={`flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded border ${
                      isSel ? 'border-transparent' : 'border-slate-300'
                    }`}
                    style={isSel ? { backgroundColor: o.color || '#64748b' } : undefined}
                  >
                    {isSel && <Check className="h-2.5 w-2.5 text-white" />}
                  </span>
                  <Tag className="h-3 w-3 shrink-0" style={{ color: o.color || '#64748b' }} />
                  <span className="truncate text-slate-700">{o.topic}</span>
                </button>
              );
            })
          )}
        </div>,
        document.body,
      )}
    </div>
  );
}

interface TopicSearchPickerProps {
  options: SubscriptionTopic[];
  excludedIds?: number[];
  onSelect: (topicId: number) => void;
  onCancel: () => void;
  loading?: boolean;
  disabled?: boolean;
  placeholder?: string;
  searchPlaceholder?: string;
  /** Open the list above the trigger (default: true for side-card pickers). */
  dropdownUp?: boolean;
}

/** Searchable single-select picker for attaching an existing catalog topic. */
export function TopicSearchPicker({
  options,
  excludedIds = [],
  onSelect,
  onCancel,
  loading = false,
  disabled = false,
  placeholder,
  searchPlaceholder,
  dropdownUp = true,
}: TopicSearchPickerProps) {
  const { t } = useUiLocale();
  const resolvedPlaceholder = placeholder ?? t('topicSearchPicker.placeholder');
  const resolvedSearchPlaceholder = searchPlaceholder ?? t('topicSearchPicker.search');
  const [open, setOpen] = useState(true);
  const [query, setQuery] = useState('');
  const [position, setPosition] = useState<PanelPosition | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const excluded = useMemo(() => new Set(excludedIds), [excludedIds]);

  const available = useMemo(
    () => options.filter((o) => !excluded.has(o.id)),
    [options, excluded],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return available;
    return available.filter((o) => o.topic.toLowerCase().includes(q));
  }, [available, query]);

  const updatePosition = useCallback(() => {
    if (triggerRef.current) {
      setPosition(computePanelPosition(triggerRef.current.getBoundingClientRect(), dropdownUp));
    }
  }, [dropdownUp]);

  useLayoutEffect(() => {
    if (open) updatePosition();
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return;
    searchRef.current?.focus();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (rootRef.current?.contains(target)) return;
      if (panelRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const pick = (id: number) => {
    if (disabled) return;
    onSelect(id);
    setOpen(false);
  };

  return (
    <div ref={rootRef} className="flex min-w-0 flex-1 items-center gap-1">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={disabled || loading}
        className="flex min-w-0 flex-1 items-center justify-between gap-1 rounded-lg border border-white/60 bg-white/70 px-2.5 py-1.5 text-sm shadow-sm backdrop-blur-sm transition-colors hover:bg-white/90 focus:outline-none focus:ring-1 focus:ring-blue-200/60 disabled:opacity-60"
      >
        {loading ? (
          <span className="flex items-center gap-1.5 text-slate-500">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            {t('common.loading')}
          </span>
        ) : (
          <span className="truncate text-slate-500">{resolvedPlaceholder}</span>
        )}
        <ChevronDown className="h-3.5 w-3.5 shrink-0 text-slate-500" />
      </button>
      <button
        type="button"
        className="aa-round-btn"
        title={t('common.cancel')}
        aria-label={t('common.cancel')}
        onClick={onCancel}
        disabled={disabled}
      >
        ×
      </button>

      {open && position && !loading && createPortal(
        <div
          ref={panelRef}
          style={{
            position: 'fixed',
            top: position.top,
            left: position.left,
            width: position.width,
            maxHeight: position.maxHeight,
            transform: position.openUp ? 'translateY(-100%)' : undefined,
          }}
          className="z-50 overflow-hidden rounded-lg border border-white/60 bg-white/95 shadow-lg backdrop-blur-xl"
        >
          <div className="border-b border-slate-100 p-1.5">
            <div className="flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2 py-1">
              <Search className="h-3.5 w-3.5 shrink-0 text-slate-400" />
              <input
                ref={searchRef}
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={resolvedSearchPlaceholder}
                className="min-w-0 flex-1 border-0 bg-transparent p-0 text-xs outline-none placeholder:text-slate-400"
                onKeyDown={(e) => {
                  if (e.key === 'Escape') onCancel();
                }}
              />
            </div>
          </div>
          <div className="max-h-48 overflow-y-auto p-1">
            {available.length === 0 ? (
              <p className="px-2 py-3 text-center text-xs text-slate-500">
                {t('topicSearchPicker.allAttached')}
              </p>
            ) : filtered.length === 0 ? (
              <p className="px-2 py-3 text-center text-xs text-slate-500">
                {t('topicSearchPicker.noMatch')}
              </p>
            ) : (
              filtered.map((o) => (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => pick(o.id)}
                  disabled={disabled}
                  className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs hover:bg-slate-100 disabled:opacity-50"
                >
                  <Tag className="h-3 w-3 shrink-0" style={{ color: o.color || '#64748b' }} />
                  <span className="truncate text-slate-700">{o.topic}</span>
                </button>
              ))
            )}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
